import { Box } from '@material-ui/core';
import useCurrencyFormat from '@src/hooks/useCurrencyFormat';
import {
  removeProductFromShoppingCart,
  updateProductShoppingCart,
} from '@src/reducers/shopify';
import {
  getShopifyCurrentStoreCanAddItemOutOfStock,
  getVariantQuantityById,
} from '@src/selectors/shopify';
import type { RootState } from '@src/store/reduxstore';
import { ShopifyProductOrigin, ShoppingCartProduct } from '@src/types/shopify';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ShoppingCartItemCounter from './ShoppingCartItemCounter';
import { RemoveItemIcon } from './SvgIcons';
import './index.css';

interface IShoppingCartItemWrapper {
  item: ShoppingCartProduct;
}

const ShoppingCartItemWrapper: React.FC<IShoppingCartItemWrapper> = ({
  item,
}) => {
  const dispatch = useDispatch();

  const [disableAddButton, setDisableAddButton] = useState(false);
  const { productVariantId, id: cartProductId } = item;
  const quantity = useSelector((state: RootState) =>
    getVariantQuantityById(state, productVariantId || '')
  );
  const canAddItemOutOfStock = useSelector(
    getShopifyCurrentStoreCanAddItemOutOfStock
  );

  const handleClickRemoveItem = () => {
    if (!cartProductId) {
      // eslint-disable-next-line no-console
      console.error('cartProductId is required');
      return;
    }
    dispatch(
      removeProductFromShoppingCart({
        cartLinesId: [cartProductId],
      })
    );
  };

  const handleUpdateItem = (newQuantity: number) => {
    if (!productVariantId || !cartProductId) {
      // eslint-disable-next-line no-console
      console.error('productVariantId and cartProductId are required');
      return;
    }

    setDisableAddButton(newQuantity >= quantity && !canAddItemOutOfStock);

    dispatch(
      updateProductShoppingCart({
        variantId: productVariantId,
        quantity: newQuantity,
        cartLinesId: [cartProductId],
        origin: ShopifyProductOrigin.LIST,
      })
    );
  };

  useEffect(() => {
    setDisableAddButton(item.quantity >= quantity && !canAddItemOutOfStock);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ShoppingCartItem
      item={item}
      disableAddButton={disableAddButton}
      handleClickRemoveItem={handleClickRemoveItem}
      handleUpdateItem={handleUpdateItem}
    />
  );
};

export type IShoppingCartItem = {
  item: ShoppingCartProduct;
  disableAddButton: boolean;
  handleClickRemoveItem: () => void;
  handleUpdateItem: (newQuantity: number) => void;
};

export const ShoppingCartItem: React.FC<IShoppingCartItem> = ({
  item,
  disableAddButton,
  handleClickRemoveItem,
  handleUpdateItem,
}) => {
  const {
    image,
    price,
    quantity,
    isFirst,
    isLast,
    titleVariant,
    titleProduct,
  } = item;

  let className = 'item';
  if (isFirst) className = 'first-item';
  if (isLast) className = 'last-item';
  if (isFirst && isLast) className = 'middle-item';

  const { amount, currencyCode } = price;
  const { url: imageUrl } = image;
  const amountInt = Number(amount);

  const priceFormatted = useCurrencyFormat({
    value: amountInt,
    currency: currencyCode,
  });
  const subtotalPriceFormatted = useCurrencyFormat({
    value: amountInt * quantity,
    currency: currencyCode,
  });

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      padding="4px 8px"
      height="96px"
      position="relative"
      className={className}
      style={{
        gap: '8px',
      }}
    >
      <img
        src={imageUrl}
        alt="product"
        style={{
          opacity: '0.8',
          borderRadius: '8px',
          boxShadow: '0px 8px 8px rgba(0, 0, 0, 0.08)',
          objectFit: 'contain',
          width: '100%',
          height: '100%',
          maxWidth: '80px',
        }}
      />
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        height="100%"
        paddingTop="4px"
      >
        <Box display="flex" justifyContent="space-between">
          <Box
            fontSize={12}
            fontWeight={500}
            className="one-line"
            style={{ textTransform: 'capitalize' }}
          >
            {titleProduct}
          </Box>
          <Box
            textAlign="right"
            onClick={handleClickRemoveItem}
            style={{ cursor: 'pointer' }}
            data-testid="shopping-cart-item-remove-button"
          >
            {RemoveItemIcon}
          </Box>
        </Box>
        <Box>
          <Box fontSize="10px" lineHeight={1.5} className="one-line">
            {titleVariant}
          </Box>

          <Box
            color="text.primary"
            fontSize="10px"
            lineHeight="120%"
            fontWeight="500"
            paddingTop={0.5}
            className="one-line"
          >
            {priceFormatted}
          </Box>
        </Box>
      </Box>
      <Box position="absolute" right={8} bottom={5}>
        <Box display="flex" flexDirection="column" textAlign="center">
          <Box display="flex" justifyContent="right">
            <ShoppingCartItemCounter
              total={quantity}
              handleClickDecrease={() =>
                handleUpdateItem(Math.max(quantity - 1, 0))
              }
              handleClickIncrease={() => handleUpdateItem(quantity + 1)}
              disableAddButton={disableAddButton}
            />
          </Box>
          <Box
            color="text.primary"
            fontSize="12px"
            lineHeight="130%"
            fontWeight="500"
            paddingTop={0.5}
            className="one-line"
          >
            {subtotalPriceFormatted}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ShoppingCartItemWrapper;
